import { Alert, FormSection, Input, Label } from "@simplicity/ui";
import { useParams } from "react-router";
import { useConnectorWorkspace, wrapperSourceLine } from "./connectorWorkspace";

interface AdvancedOptionsProps {
  vars: Record<string, unknown>;
  onVarsChange: (vars: Record<string, unknown>) => void;
  connectorType: string;
  connectorPrefix: string;
  clearBanner: () => void;
}

function str(vars: Record<string, unknown>, key: string): string {
  const v = vars[key];
  return typeof v === "string" ? v : "";
}

function num(vars: Record<string, unknown>, key: string): number | "" {
  const v = vars[key];
  return typeof v === "number" ? v : "";
}

function useFieldUpdater(props: AdvancedOptionsProps) {
  function updateField(key: string, value: string) {
    const next = { ...props.vars };
    if (value) next[key] = value;
    else delete next[key];
    props.onVarsChange(next);
    props.clearBanner();
  }

  function updateNumber(key: string, value: string) {
    const next = { ...props.vars };
    if (value) next[key] = Number(value);
    else delete next[key];
    props.onVarsChange(next);
    props.clearBanner();
  }

  return { updateField, updateNumber };
}

function DatasetFields({
  vars,
  prefix,
  updateField,
}: {
  vars: Record<string, unknown>;
  prefix: string;
  updateField: (k: string, v: string) => void;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex-1">
        <Label htmlFor="adv-bronze-dataset" className="form-label">
          Bronze Dataset
        </Label>
        <Input
          id="adv-bronze-dataset"
          type="text"
          value={str(vars, "bronzeDataset")}
          onChange={(e) => updateField("bronzeDataset", e.target.value)}
          placeholder={`Default: bronze__${prefix}`}
          className="font-mono"
        />
      </div>
      <div className="flex-1">
        <Label htmlFor="adv-silver-dataset" className="form-label">
          Silver Dataset
        </Label>
        <Input
          id="adv-silver-dataset"
          type="text"
          value={str(vars, "silverDataset")}
          onChange={(e) => updateField("silverDataset", e.target.value)}
          placeholder={`Default: silver__${prefix}`}
          className="font-mono"
        />
      </div>
    </div>
  );
}

function GcsFields({
  vars,
  updateField,
}: {
  vars: Record<string, unknown>;
  updateField: (k: string, v: string) => void;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex-1">
        <Label htmlFor="adv-gcs-bucket" className="form-label">
          GCS Bucket Override
        </Label>
        <Input
          id="adv-gcs-bucket"
          type="text"
          value={str(vars, "gcsBucketOverride")}
          onChange={(e) => updateField("gcsBucketOverride", e.target.value)}
          placeholder="Optional — override derived bucket"
          className="font-mono"
        />
      </div>
      <div className="flex-1">
        <Label htmlFor="adv-gcs-prefix" className="form-label">
          GCS Prefix Override
        </Label>
        <Input
          id="adv-gcs-prefix"
          type="text"
          value={str(vars, "gcsPrefixOverride")}
          onChange={(e) => updateField("gcsPrefixOverride", e.target.value)}
          placeholder="Optional — override derived prefix"
          className="font-mono"
        />
      </div>
    </div>
  );
}

function DeansListAdvanced(props: AdvancedOptionsProps) {
  const { updateNumber, updateField } = useFieldUpdater(props);
  return (
    <>
      <div className="flex gap-4">
        <div className="flex-1">
          <Label htmlFor="adv-lookback" className="form-label">
            Lookback Days
          </Label>
          <Input
            id="adv-lookback"
            type="number"
            value={num(props.vars, "lookbackDays")}
            onChange={(e) => updateNumber("lookbackDays", e.target.value)}
            placeholder="Default: 30"
            className="font-mono"
          />
        </div>
        <div className="flex-1">
          <Label htmlFor="adv-max-days" className="form-label">
            Max Days Per Request
          </Label>
          <Input
            id="adv-max-days"
            type="number"
            value={num(props.vars, "maxDaysPerRequest")}
            onChange={(e) => updateNumber("maxDaysPerRequest", e.target.value)}
            placeholder="Default: 30"
            className="font-mono"
          />
        </div>
      </div>
      <DatasetFields vars={props.vars} prefix={props.connectorPrefix} updateField={updateField} />
    </>
  );
}

function SftpAdvanced(props: AdvancedOptionsProps) {
  const { updateField } = useFieldUpdater(props);
  return (
    <>
      <GcsFields vars={props.vars} updateField={updateField} />
      <DatasetFields vars={props.vars} prefix={props.connectorPrefix} updateField={updateField} />
    </>
  );
}

function SchoolMintAdvanced(props: AdvancedOptionsProps) {
  const { updateField, updateNumber } = useFieldUpdater(props);
  return (
    <>
      <GcsFields vars={props.vars} updateField={updateField} />
      <div className="flex gap-4">
        <div className="flex-1">
          <Label htmlFor="adv-poll-interval" className="form-label">
            Poll Interval (ms)
          </Label>
          <Input
            id="adv-poll-interval"
            type="number"
            value={num(props.vars, "pollIntervalMs")}
            onChange={(e) => updateNumber("pollIntervalMs", e.target.value)}
            placeholder="Default: 30000"
            className="font-mono"
          />
        </div>
        <div className="flex-1">
          <Label htmlFor="adv-poll-max" className="form-label">
            Poll Max Attempts
          </Label>
          <Input
            id="adv-poll-max"
            type="number"
            value={num(props.vars, "pollMaxAttempts")}
            onChange={(e) => updateNumber("pollMaxAttempts", e.target.value)}
            placeholder="Default: 60"
            className="font-mono"
          />
        </div>
      </div>
      <DatasetFields vars={props.vars} prefix={props.connectorPrefix} updateField={updateField} />
    </>
  );
}

function IReadyAdvanced(props: AdvancedOptionsProps) {
  const { updateField, updateNumber } = useFieldUpdater(props);
  return (
    <>
      <div className="flex gap-4">
        <div className="flex-1">
          <Label htmlFor="adv-host" className="form-label">
            SFTP Host
          </Label>
          <Input
            id="adv-host"
            type="text"
            value={str(props.vars, "host")}
            onChange={(e) => updateField("host", e.target.value)}
            placeholder="Optional — override default host"
            className="font-mono"
          />
        </div>
        <div className="flex-1">
          <Label htmlFor="adv-port" className="form-label">
            SFTP Port
          </Label>
          <Input
            id="adv-port"
            type="number"
            value={num(props.vars, "port")}
            onChange={(e) => updateNumber("port", e.target.value)}
            placeholder="Default: 22"
            className="font-mono"
          />
        </div>
      </div>
      <div className="flex gap-4">
        <div className="flex-1">
          <Label htmlFor="adv-region" className="form-label">
            Region
          </Label>
          <Input
            id="adv-region"
            type="text"
            value={str(props.vars, "region")}
            onChange={(e) => updateField("region", e.target.value)}
            placeholder="Optional"
            className="font-mono"
          />
        </div>
        <div className="flex-1">
          <Label htmlFor="adv-sftp-wrapper" className="form-label">
            SFTP Wrapper URL
          </Label>
          <Input
            id="adv-sftp-wrapper"
            type="text"
            value={str(props.vars, "sftpWrapperUrl")}
            onChange={(e) => updateField("sftpWrapperUrl", e.target.value)}
            placeholder="Optional"
            className="font-mono"
          />
        </div>
      </div>
      <DatasetFields vars={props.vars} prefix={props.connectorPrefix} updateField={updateField} />
    </>
  );
}

/** File-drop keeps its SFTP connection fields nested under vars.acquisition. */
function readAcquisition(vars: Record<string, unknown>): Record<string, unknown> {
  const raw = vars.acquisition;
  return raw != null && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

/** Read-only: the wrapper's project and region always come from the onsite workspace. */
function WrapperSource(props: { sftpWrapperUrl: string }) {
  const { id } = useParams<{ id: string }>();
  const workspace = useConnectorWorkspace(id);
  const line = wrapperSourceLine(workspace, props.sftpWrapperUrl);
  if (!line) return null;
  if (line.warning) return <Alert variant="warning">{line.text}</Alert>;
  return <p className="text-sm text-muted-foreground">{line.text}</p>;
}

function FileDropAdvanced(props: AdvancedOptionsProps) {
  const { updateField } = useFieldUpdater(props);
  const acq = readAcquisition(props.vars);
  const acqStr = (key: string) => (typeof acq[key] === "string" ? acq[key] : "");
  const acqNum = (key: string): number | "" => (typeof acq[key] === "number" ? acq[key] : "");

  function updateAcq(key: string, value: string | number) {
    const next = { ...acq };
    if (value === "" || value === undefined) delete next[key];
    else next[key] = value;
    next.mode = "remote-sftp";
    props.onVarsChange({ ...props.vars, acquisition: next });
    props.clearBanner();
  }

  return (
    <>
      <div className="flex gap-4">
        <div className="flex-1">
          <Label htmlFor="adv-fd-host" className="form-label">
            SFTP Host
          </Label>
          <Input
            id="adv-fd-host"
            type="text"
            value={acqStr("host")}
            onChange={(e) => updateAcq("host", e.target.value)}
            placeholder="Required — SFTP host"
            className="font-mono"
          />
        </div>
        <div className="flex-1">
          <Label htmlFor="adv-fd-port" className="form-label">
            SFTP Port
          </Label>
          <Input
            id="adv-fd-port"
            type="number"
            value={acqNum("port")}
            onChange={(e) => updateAcq("port", e.target.value ? Number(e.target.value) : "")}
            placeholder="Default: 22"
            className="font-mono"
          />
        </div>
      </div>
      <WrapperSource sftpWrapperUrl={acqStr("sftpWrapperUrl")} />
      <div className="flex gap-4">
        <div className="flex-1">
          <Label htmlFor="adv-fd-sftp-wrapper" className="form-label">
            SFTP Wrapper URL
          </Label>
          <Input
            id="adv-fd-sftp-wrapper"
            type="text"
            value={acqStr("sftpWrapperUrl")}
            onChange={(e) => updateAcq("sftpWrapperUrl", e.target.value)}
            placeholder="Optional"
            className="font-mono"
          />
        </div>
      </div>
      <DatasetFields vars={props.vars} prefix={props.connectorPrefix} updateField={updateField} />
    </>
  );
}

function DefaultAdvanced(props: AdvancedOptionsProps) {
  const { updateField } = useFieldUpdater(props);
  return (
    <DatasetFields vars={props.vars} prefix={props.connectorPrefix} updateField={updateField} />
  );
}

export function ConnectorAdvancedOptions(props: AdvancedOptionsProps) {
  function renderContent() {
    switch (props.connectorType) {
      case "deanslist":
        return <DeansListAdvanced {...props} />;
      case "schoolmint":
        return <SchoolMintAdvanced {...props} />;
      case "schoolmint-enroll":
      case "avela":
        return <SftpAdvanced {...props} />;
      case "iready-sftp":
        return <IReadyAdvanced {...props} />;
      case "file-drop":
        return <FileDropAdvanced {...props} />;
      default:
        return <DefaultAdvanced {...props} />;
    }
  }

  return (
    <FormSection title="Advanced Options" collapsible className="mb-4">
      <div className="space-y-4">{renderContent()}</div>
    </FormSection>
  );
}
